import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import SuizaDeterminacionEditor from "@/pages/SuizaDeterminacionEditor";
import SuizaFirmasModal from "@/pages/SuizaFirmasModal";
import { PenLine } from "lucide-react";
import SuizaCuestionarioTecnico from "@/pages/SuizaCuestionarioTecnico";
import SuizaVerificacionPrevia from "@/pages/SuizaVerificacionPrevia";
import SuizaCentros from "@/pages/SuizaCentros";
import SuizaPuestos from "@/pages/SuizaPuestos";
import SuizaInspecciones from "@/pages/SuizaInspecciones";
import SuizaSeguimiento from "@/pages/SuizaSeguimiento";
import {
  chGetEmpresa, chUpdateEmpresa, chGetMeta, chListDocumentos, chGenerarDocumento, chBorrarDocumento,
  chDeleteEmpresa, chListFormularios, chCrearFormulario, chBorrarFormulario, chImportarFormulario,
  chGenerarPlanConsolidado,
} from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Save, Sparkles, FileText, Loader2, Trash2, AlertTriangle, Languages,
  Link2, Copy, MessageCircle, Mail, Plus, DownloadCloud, Image as ImageIcon, Check, Pencil, X, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SMART_FORMS = new Set(["determinacion_peligros", "plan_emergencia", "concepto_seguridad",
  "procedimiento_trabajo", "formacion_informacion", "entrega_epi", "investigacion_accidente", "inspeccion_seguridad"]);
const field = "w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm";
const TABS = [
  { k: "datos", label: "Datos" },
  { k: "centros", label: "Centros" },
  { k: "puestos", label: "Puestos" },
  { k: "inspecciones", label: "Inspecciones" },
  { k: "documentos", label: "Documentos" },
  { k: "seguimiento", label: "Seguimiento" },
];
const PERFIL_CAMPOS = [
  ["descripcion", "Descripción de la actividad", "¿A qué se dedica la empresa? procesos principales…"],
  ["puestos", "Puestos de trabajo", "Lista de puestos y sus tareas habituales"],
  ["maquinaria", "Maquinaria y equipos", "Máquinas, herramientas y equipos utilizados"],
  ["instalaciones", "Instalaciones", "Locales, almacenes, zonas, instalaciones eléctricas/gas…"],
  ["procesos", "Procesos y productos", "Procesos de trabajo, productos químicos si los hay…"],
  ["notas", "Notas adicionales", "Cualquier información relevante para la IA"],
];

export default function SuizaEmpresaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [perfil, setPerfil] = useState({});
  const [tipos, setTipos] = useState([]);
  const [meta, setMeta] = useState({ cantones: [], tipos_organizacion: [] });
  const [docs, setDocs] = useState([]);
  const [gen, setGen] = useState({ tipo: "", idioma: "" });
  const [forms, setForms] = useState([]);
  const [creando, setCreando] = useState(false);
  const [idiomaForm, setIdiomaForm] = useState("fr");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [detEditor, setDetEditor] = useState(null);
  const [firmasDoc, setFirmasDoc] = useState(null);
  const [verif, setVerif] = useState(null);
  const [batch, setBatch] = useState({ running: false, idioma: "", done: 0, total: 0 });
  const [activeTab, setActiveTab] = useState("datos");
  const [consol, setConsol] = useState({ running: false, idioma: "fr" });

  const loadDocs = useCallback(() => chListDocumentos(id).then(setDocs).catch(() => {}), [id]);
  const generarConsolidado = async () => {
    setConsol((s) => ({ ...s, running: true }));
    try {
      const d = await chGenerarPlanConsolidado(id, { idioma: consol.idioma });
      toast.success("Plan consolidado generado");
      await loadDocs();
      if (d.pdf_url) window.open(`${process.env.REACT_APP_BACKEND_URL}${d.pdf_url}`, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el plan"); }
    finally { setConsol((s) => ({ ...s, running: false })); }
  };
  const loadForms = useCallback(() => chListFormularios(id).then(setForms).catch(() => {}), [id]);
  useEffect(() => {
    chGetEmpresa(id).then((e) => { setEmpresa(e); setPerfil(e.perfil || {}); }).catch(() => {});
    chGetMeta().then((m) => { setTipos(m.tipos_documento || []); setMeta(m); }).catch(() => {});
    loadDocs();
    loadForms();
  }, [id, loadDocs, loadForms]);

  const abrirEdicion = () => {
    setEditForm({
      razon_social: empresa.razon_social || "", canton: empresa.canton || "",
      actividad: empresa.actividad || "", direccion: empresa.direccion || "",
      contacto: empresa.contacto || "", num_trabajadores: empresa.num_trabajadores || "",
      tipo_organizacion: empresa.tipo_organizacion || "",
    });
    setEditOpen(true);
  };
  const guardarEdicion = async () => {
    if (!(editForm.razon_social || "").trim()) { toast.error("La razón social es obligatoria"); return; }
    setSavingEdit(true);
    try {
      const e = await chUpdateEmpresa(id, editForm);
      setEmpresa(e); setEditOpen(false); toast.success("Datos de la empresa actualizados");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingEdit(false); }
  };
  const eliminarEmpresa = async () => {
    if (!window.confirm(`¿Eliminar la empresa "${empresa.razon_social}"? Se borrarán también sus documentos y cuestionarios. Esta acción no se puede deshacer.`)) return;
    try { await chDeleteEmpresa(id); toast.success("Empresa eliminada"); navigate("/ch"); }
    catch { toast.error("No se pudo eliminar la empresa"); }
  };

  const publicLink = (token) => `${window.location.origin}/ch/f/${token}`;
  const crearForm = async () => {
    setCreando(true);
    try {
      const { token } = await chCrearFormulario({ empresa_id: id, idioma: idiomaForm });
      await loadForms();
      try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace creado y copiado"); }
      catch { toast.success("Enlace creado"); }
    } catch { toast.error("No se pudo crear el formulario"); }
    finally { setCreando(false); }
  };
  const copiarForm = async (token) => {
    try { await navigator.clipboard.writeText(publicLink(token)); toast.success("Enlace copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };
  const borrarForm = async (fid) => {
    try { await chBorrarFormulario(fid); await loadForms(); toast.success("Formulario eliminado"); }
    catch { toast.error("No se pudo eliminar"); }
  };
  const importarForm = async (fid) => {
    try {
      await chImportarFormulario(fid);
      const e = await chGetEmpresa(id); setEmpresa(e); setPerfil(e.perfil || {});
      await loadForms();
      toast.success("Datos del cliente importados al perfil de la empresa");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo importar"); }
  };
  const msgWa = (token) => encodeURIComponent(`Bonjour, afin de préparer votre documentation de sécurité au travail, merci de compléter ce court questionnaire : ${publicLink(token)}`);
  const cuerpoMail = (token) => encodeURIComponent(`Bonjour,\n\nAfin de préparer votre documentation de sécurité au travail, merci de compléter ce questionnaire :\n\n${publicLink(token)}\n\nMerci de votre collaboration.`);

  const guardarPerfil = async () => {
    try { await chUpdateEmpresa(id, { perfil }); toast.success("Datos guardados"); }
    catch { toast.error("No se pudo guardar"); }
  };

  const generar = async (tipo, idioma, extra) => {
    setGen({ tipo, idioma });
    try {
      await chUpdateEmpresa(id, { perfil });
      const d = await chGenerarDocumento(id, tipo, idioma, extra);
      await loadDocs();
      const avisos = (d.datos_faltantes?.length || 0) + (d.incertidumbres?.length || 0);
      if (avisos) toast.warning(`Documento generado con ${avisos} aviso(s) para revisión técnica`);
      else toast.success("Documento generado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el documento"); }
    finally { setGen({ tipo: "", idioma: "" }); }
  };

  const generarTodos = async (idioma) => {
    const lista = tipos.filter((t) => !SMART_FORMS.has(t.codigo));
    if (!lista.length || gen.tipo) return;
    setBatch({ running: true, idioma, done: 0, total: lista.length });
    try { await chUpdateEmpresa(id, { perfil }); } catch (e) { console.error("No se pudo guardar el perfil de la empresa:", e); }
    const errores = [];
    for (const t of lista) {
      setGen({ tipo: t.codigo, idioma });
      try { await chGenerarDocumento(id, t.codigo, idioma); }
      catch { errores.push(t.es); }
      setBatch((b) => ({ ...b, done: b.done + 1 }));
    }
    setGen({ tipo: "", idioma: "" });
    await loadDocs();
    setBatch({ running: false, idioma: "", done: 0, total: 0 });
    if (errores.length) toast.warning(`Generados ${lista.length - errores.length}/${lista.length}. Fallaron: ${errores.join(", ")}`);
    else toast.success(`${lista.length} documentos generados`);
  };

  const borrar = async (d) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    await chBorrarDocumento(d.id); toast.success("Documento eliminado"); loadDocs();
  };

  if (!empresa) return <Layout title="Cargando…" />;

  return (
    <Layout title={`${empresa.razon_social} 🇨🇭`} subtitle={`${empresa.canton} · ${empresa.tipo_organizacion || ""}`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={abrirEdicion} data-testid="ch-editar-empresa" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">
            <Pencil className="w-4 h-4" /> Editar datos
          </button>
          <button onClick={eliminarEmpresa} data-testid="ch-eliminar-empresa" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50">
            <Trash2 className="w-4 h-4" /> Eliminar
          </button>
        </div>
      }
    >
      <button onClick={() => navigate("/ch")} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 mb-5">
        <ArrowLeft className="w-4 h-4" /> Volver a empresas
      </button>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="ch-empresa-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setActiveTab(t.k)} data-testid={`ch-tab-${t.k}`}
            className={`shrink-0 px-4 h-11 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === t.k ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="ch-editar-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Editar datos de la empresa</h3>
              <button onClick={() => setEditOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><label className="text-sm font-medium text-slate-700">Razón social *</label>
                <input data-testid="ch-edit-razon" value={editForm.razon_social || ""} onChange={(e) => setEditForm((s) => ({ ...s, razon_social: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Cantón</label>
                  <select data-testid="ch-edit-canton" value={editForm.canton || ""} onChange={(e) => setEditForm((s) => ({ ...s, canton: e.target.value }))} className={field + " mt-1 h-11"}>
                    {(meta.cantones || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className="text-sm font-medium text-slate-700">Tipo</label>
                  <select data-testid="ch-edit-tipo" value={editForm.tipo_organizacion || ""} onChange={(e) => setEditForm((s) => ({ ...s, tipo_organizacion: e.target.value }))} className={field + " mt-1 h-11"}>
                    {(meta.tipos_organizacion || []).map((t) => <option key={t} value={t}>{t}</option>)}
                  </select></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Actividad</label>
                <input data-testid="ch-edit-actividad" value={editForm.actividad || ""} onChange={(e) => setEditForm((s) => ({ ...s, actividad: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700">Dirección</label>
                  <input data-testid="ch-edit-direccion" value={editForm.direccion || ""} onChange={(e) => setEditForm((s) => ({ ...s, direccion: e.target.value }))} className={field + " mt-1 h-11"} /></div>
                <div><label className="text-sm font-medium text-slate-700">Persona de contacto</label>
                  <input data-testid="ch-edit-contacto" value={editForm.contacto || ""} onChange={(e) => setEditForm((s) => ({ ...s, contacto: e.target.value }))} className={field + " mt-1 h-11"} /></div>
              </div>
              <div><label className="text-sm font-medium text-slate-700">Nº de trabajadores</label>
                <input type="number" min="0" data-testid="ch-edit-num" value={editForm.num_trabajadores || ""} onChange={(e) => setEditForm((s) => ({ ...s, num_trabajadores: e.target.value }))} className={field + " mt-1 h-11"} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditOpen(false)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={guardarEdicion} disabled={savingEdit} data-testid="ch-edit-guardar" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 disabled:opacity-50">
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "centros" && <SuizaCentros empresaId={id} cantones={meta.cantones || []} />}
      {activeTab === "puestos" && <SuizaPuestos empresaId={id} />}
      {activeTab === "inspecciones" && <SuizaInspecciones empresaId={id} />}
      {activeTab === "seguimiento" && <SuizaSeguimiento empresaId={id} />}

      {activeTab === "datos" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">Datos para la IA</h3>
            <button onClick={guardarPerfil} data-testid="ch-guardar-perfil" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">
              <Save className="w-4 h-4" /> Guardar
            </button>
          </div>
          <div className="space-y-3">
            {PERFIL_CAMPOS.map(([k, label, ph]) => (
              <div key={k}>
                <label className="text-sm font-medium text-slate-700">{label}</label>
                <textarea data-testid={`ch-perfil-${k}`} rows={2} value={perfil[k] || ""} placeholder={ph}
                  onChange={(e) => setPerfil((s) => ({ ...s, [k]: e.target.value }))} className={field + " mt-1"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "documentos" && (
        <div>
          <div className="bg-white border border-blue-200 rounded-xl p-5 mb-4" data-testid="ch-consolidado-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5" /></div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg">Plan de sécurité consolidado</h3>
                <p className="text-sm text-slate-500 mt-1">Genera el Plan de seguridad y protección de la salud a nivel de empresa, integrando los peligros y medidas de las inspecciones de todos los centros (cada elemento identifica su centro).</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <select value={consol.idioma} onChange={(e) => setConsol((s) => ({ ...s, idioma: e.target.value }))} data-testid="ch-consolidado-idioma" className="h-10 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                  </select>
                  <button onClick={generarConsolidado} disabled={consol.running} data-testid="ch-consolidado-generar" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
                    {consol.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Generar plan consolidado
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-2" data-testid="ch-docs-historico-aviso">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">Estos documentos pertenecen al <strong>sistema anterior</strong> (generación directa a nivel de empresa) y se conservan como <strong>histórico</strong>. La nueva generación partirá de las inspecciones por centro.</p>
          </div>

          {/* Generación de documentos */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
            <h3 className="font-heading font-semibold text-lg mb-1">Generar documento (IA)</h3>
            <p className="text-sm text-slate-500 mb-4">Elige el documento y el idioma. La IA analiza el caso y redacta contenido específico basado en normativa suiza; marca lo que requiera revisión.</p>
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-lg bg-blue-50/60 border border-blue-100" data-testid="ch-batch-bar">
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-medium text-slate-800">Generar todos (sin preguntas)</p>
                <p className="text-xs text-slate-500">Genera de una vez los documentos que no requieren verificación previa (todos excepto Determinación de peligros, Plan de emergencia y Concepto de seguridad).</p>
              </div>
              {batch.running ? (
                <span className="text-sm text-blue-700 font-medium inline-flex items-center gap-2" data-testid="ch-batch-progress">
                  <Loader2 className="w-4 h-4 animate-spin" /> {batch.done}/{batch.total} · {batch.idioma.toUpperCase()}
                </span>
              ) : (
                ["es", "fr"].map((idm) => (
                  <button key={idm} onClick={() => generarTodos(idm)} disabled={!!gen.tipo} data-testid={`ch-batch-${idm}`}
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-40">
                    <Sparkles className="w-4 h-4" /> Generar todos · {idm.toUpperCase()}
                  </button>
                ))
              )}
            </div>
            <div className="space-y-3" data-testid="ch-generar-panel">
              {tipos.map((t) => (
                <div key={t.codigo} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{t.es}</div>
                    <div className="text-xs text-slate-500">{t.fr}</div>
                  </div>
                  {["es", "fr"].map((idm) => {
                    const busy = gen.tipo === t.codigo && gen.idioma === idm;
                    return (
                      <button key={idm} onClick={() => (SMART_FORMS.has(t.codigo) ? setVerif({ tipo: t.codigo, idioma: idm }) : generar(t.codigo, idm))} disabled={!!gen.tipo}
                        data-testid={`ch-gen-${t.codigo}-${idm}`}
                        className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-blue-600 text-blue-700 text-sm font-medium hover:bg-blue-50 disabled:opacity-40">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {idm === "es" ? "ES" : "FR"}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h3 className="font-heading font-semibold text-lg">Documentos generados</h3>
            {docs.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {[...new Set(docs.map((d) => d.idioma))].sort().map((lg) => (
                  <a key={lg} href={`${BACKEND}/api/ch/empresas/${id}/documentos/zip?idioma=${lg}`} data-testid={`ch-zip-${lg}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">
                    <DownloadCloud className="w-4 h-4" /> ZIP {lg.toUpperCase()}
                  </a>
                ))}
                <a href={`${BACKEND}/api/ch/empresas/${id}/documentos/zip`} data-testid="ch-zip-todos"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">
                  <DownloadCloud className="w-4 h-4" /> Todo
                </a>
              </div>
            )}
          </div>
          {docs.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="ch-docs-vacio">
              Aún no hay documentos. Genera el primero con los botones de arriba.
            </div>
          ) : (
            <div className="space-y-3" data-testid="ch-docs-lista">
              {docs.map((d) => (
                <div key={d.id} data-testid={`ch-doc-${d.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{d.titulo}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1"><Languages className="w-3.5 h-3.5" /> {d.idioma === "fr" ? "Français" : "Español"} · {formatFecha(d.generado_at)}</div>
                    </div>
                    <a href={`${BACKEND}${d.docx_url}`} target="_blank" rel="noreferrer" data-testid={`ch-docx-${d.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"><FileText className="w-4 h-4 text-blue-700" /> Word</a>
                    <a href={`${BACKEND}${d.pdf_url}`} target="_blank" rel="noreferrer" data-testid={`ch-pdf-${d.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"><FileText className="w-4 h-4 text-red-600" /> PDF</a>
                    <button onClick={() => generar(d.tipo, d.idioma === "fr" ? "es" : "fr")} disabled={!!gen.tipo}
                      data-testid={`ch-regen-${d.id}`} title={`Generar este documento en ${d.idioma === "fr" ? "español" : "francés"}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-blue-600 text-blue-700 text-sm hover:bg-blue-50 disabled:opacity-40">
                      {gen.tipo === d.tipo && gen.idioma === (d.idioma === "fr" ? "es" : "fr") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />} {d.idioma === "fr" ? "ES" : "FR"}
                    </button>
                    {d.tipo === "determinacion_peligros" && (
                      <button onClick={() => setDetEditor(d)} data-testid={`ch-editar-det-${d.id}`} title="Revisar y validar"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-600 text-emerald-700 text-sm hover:bg-emerald-50">
                        <ShieldCheck className="w-4 h-4" /> Revisar
                      </button>
                    )}
                    {d.tipo === "formacion_informacion" && (
                      <button onClick={() => setFirmasDoc(d)} data-testid={`ch-firmas-${d.id}`} title="Registrar impartición / Firmas"
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-indigo-600 text-indigo-700 text-sm hover:bg-indigo-50">
                        <PenLine className="w-4 h-4" /> Firmas
                      </button>
                    )}
                    <button onClick={() => borrar(d)} data-testid={`ch-del-${d.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {(d.estado || d.nivel_global) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`ch-doc-badges-${d.id}`}>
                      {d.estado === "validado" ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                          {d.idioma === "fr" ? "Validé par le technicien" : "Validado por el técnico"}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          {d.idioma === "fr" ? "Projet IA – à valider" : "Borrador IA – a revisar"}
                        </span>
                      )}
                      {typeof d.peligros !== "undefined" && (d.peligros || []).length > 0 && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {(d.peligros || []).length} {d.idioma === "fr" ? "dangers" : "peligros"}
                        </span>
                      )}
                      {d.nivel_global === 2 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          {d.idioma === "fr" ? "Vérification technique" : "Verificación técnica"}
                        </span>
                      )}
                      {d.nivel_global === 3 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          {d.idioma === "fr" ? "Appréciation / MSST" : "Valoración específica / MSST"}
                        </span>
                      )}
                    </div>
                  )}
                  {(d.alertas || []).filter((a) => a.nivel >= 2).slice(0, 3).map((a, i) => (
                    <div key={i} className="mt-1.5 text-xs flex items-start gap-1.5" style={{ color: a.nivel === 3 ? "#b91c1c" : "#b45309" }} data-testid={`ch-doc-alerta-${d.id}-${i}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{a.texto}</span>
                    </div>
                  ))}
                  {((d.datos_faltantes || []).length > 0 || (d.incertidumbres || []).length > 0) && (
                    <div className="mt-2 text-xs text-amber-700 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{[...(d.datos_faltantes || []), ...(d.incertidumbres || [])].length} punto(s) a revisar/completar (detallados en el documento).</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "datos" && (
      <div className="mt-6">
        <SuizaCuestionarioTecnico empresaId={id} schema={meta.form_schema || []}
          initialData={empresa.perfil_tecnico} initialFotos={empresa.perfil_fotos}
          onSaved={(pt) => setEmpresa((e) => ({ ...e, perfil_tecnico: pt }))} />
      </div>
      )}

      {/* Formularios para el cliente */}
      {activeTab === "datos" && (
      <div className="bg-white border border-slate-200 rounded-xl p-5 mt-6" data-testid="ch-formularios">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-heading font-semibold text-lg">Cuestionarios para el cliente</h3>
            <p className="text-sm text-slate-500">Envía un enlace bilingüe (ES/FR) para que el cliente aporte sus datos y fotos. Al importar, se vuelcan al perfil de la empresa.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={idiomaForm} onChange={(e) => setIdiomaForm(e.target.value)} data-testid="ch-form-idioma-nuevo"
              className="h-10 px-3 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500">
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
            <button onClick={crearForm} disabled={creando} data-testid="ch-crear-formulario"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
              {creando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Nuevo enlace
            </button>
          </div>
        </div>
        {forms.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center text-slate-500 text-sm" data-testid="ch-forms-vacio">
            Aún no has creado ningún cuestionario para este cliente.
          </div>
        ) : (
          <div className="space-y-3" data-testid="ch-forms-lista">
            {forms.map((f) => (
              <div key={f.id} data-testid={`ch-form-item-${f.id}`} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700 uppercase">{f.idioma}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600">{f.estado}</span>
                  <span className="ml-auto flex items-center gap-3 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {f.num_campos}</span>
                    <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {f.num_fotos}</span>
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <a href={publicLink(f.token)} target="_blank" rel="noreferrer" className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-500 truncate max-w-[240px] inline-flex items-center gap-1"><Link2 className="w-3 h-3" />{publicLink(f.token)}</a>
                  <button onClick={() => copiarForm(f.token)} data-testid={`ch-form-copiar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Copy className="w-3.5 h-3.5" /> Copiar</button>
                  <a href={`https://wa.me/?text=${msgWa(f.token)}`} target="_blank" rel="noreferrer" data-testid={`ch-form-wa-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-emerald-300 text-xs font-medium text-emerald-700 hover:bg-emerald-50"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
                  <a href={`mailto:?subject=${encodeURIComponent("Questionnaire de sécurité au travail")}&body=${cuerpoMail(f.token)}`} data-testid={`ch-form-mail-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100"><Mail className="w-3.5 h-3.5" /> Email</a>
                  {(f.estado === "completado" || f.estado === "en_progreso") && (
                    <button onClick={() => importarForm(f.id)} data-testid={`ch-form-importar-${f.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-blue-600 text-xs font-medium text-blue-700 hover:bg-blue-50"><DownloadCloud className="w-3.5 h-3.5" /> Importar al perfil</button>
                  )}
                  {f.estado === "revisado" && <span className="text-xs text-emerald-700 font-medium inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Importado</span>}
                  <button onClick={() => borrarForm(f.id)} data-testid={`ch-form-borrar-${f.id}`} className="ml-auto w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}
      {firmasDoc && <SuizaFirmasModal doc={firmasDoc} onClose={() => setFirmasDoc(null)} />}
      {detEditor && (
        <SuizaDeterminacionEditor doc={detEditor} onClose={() => setDetEditor(null)}
          onSaved={() => { loadDocs(); }} />
      )}
      {verif && (
        <SuizaVerificacionPrevia empresaId={id} tipo={verif.tipo} idioma={verif.idioma}
          onClose={() => setVerif(null)}
          onGenerate={async (extra, tipoOverride) => { await generar(tipoOverride || verif.tipo, verif.idioma, extra); setVerif(null); }} />
      )}
    </Layout>
  );
}
